/**
 * Loading skeleton components for content placeholders
 */

interface SkeletonProps {
  className?: string;
  style?: React.CSSProperties;
}

export function Skeleton({ className = '', style }: SkeletonProps) {
  return <div className={`skeleton ${className}`} style={style} />;
}

export function SkeletonText({ lines = 3, className = '' }: { lines?: number; className?: string }) {
  return (
    <div className={className}>
      {Array.from({ length: lines }).map((_, i) => (
        <div key={i} className="skeleton skeleton-text" />
      ))}
    </div>
  );
}

export function SkeletonConversationItem() {
  return (
    <div className="skeleton-conversation-item">
      <div className="skeleton skeleton-conversation-icon" />
      <div className="skeleton skeleton-conversation-name" />
    </div>
  );
}

export function SkeletonConversationList({ count = 4 }: { count?: number }) {
  return (
    <div>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonConversationItem key={i} />
      ))}
    </div>
  );
}

export function SkeletonMessage({ isUser = false }: { isUser?: boolean }) {
  return (
    <div className={`skeleton-message ${isUser ? 'user' : ''}`}>
      {!isUser && <div className="skeleton skeleton-avatar" />}
      <div className="skeleton-message-content">
        <div className="skeleton skeleton-text" style={{ width: isUser ? '60%' : '80%' }} />
        <div className="skeleton skeleton-text" style={{ width: isUser ? '40%' : '60%' }} />
      </div>
      {isUser && <div className="skeleton skeleton-avatar" />}
    </div>
  );
}

export function SkeletonChatMessages({ count = 4 }: { count?: number }) {
  return (
    <div style={{ padding: '16px' }}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonMessage key={i} isUser={i % 3 === 1} />
      ))}
    </div>
  );
}

export function SkeletonNoteCard() {
  return (
    <div className="skeleton-note-card">
      <div className="skeleton skeleton-note-title" />
      <div className="skeleton skeleton-note-preview" />
      <div className="skeleton skeleton-note-preview" />
      <div className="skeleton skeleton-note-preview" />
    </div>
  );
}

export function SkeletonNoteList({ count = 3 }: { count?: number }) {
  return (
    <div>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonNoteCard key={i} />
      ))}
    </div>
  );
}

export function SkeletonCard({ height = 120 }: { height?: number }) {
  return (
    <div className="skeleton-card">
      <div className="skeleton skeleton-title" />
      <SkeletonText lines={2} />
    </div>
  );
}

export function SkeletonObjectList({ count = 5 }: { count?: number }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px' }}>
          <div className="skeleton" style={{ width: 24, height: 24, borderRadius: 6 }} />
          <div className="skeleton" style={{ flex: 1, height: 16 }} />
          <div className="skeleton" style={{ width: 60, height: 14 }} />
        </div>
      ))}
    </div>
  );
}
